/**
 * ContactDialogProvider — owns the SINGLE shared contact dialog for the landing page.
 *
 * Both landing CTAs ("Schedule a demo" in FinalCtaSection and "Talk to us about your
 * study" in HeroSection) open the same DemoRequestDialog. Rather than each section
 * holding its own dialog instance + state, this provider renders ONE dialog and
 * exposes an imperative `open(source)` via the `useContactDialog()` hook. The `source`
 * tags which CTA opened it (drives the dialog copy + the email subject server-side).
 *
 * Wrap the landing tree once (see RetroLanding) and call `useContactDialog().open(...)`
 * from any descendant.
 */
import * as React from 'react';
import DemoRequestDialog, { ContactSource } from './DemoRequestDialog';

interface ContactDialogContextValue {
  /** Open the shared contact dialog. `source` defaults to 'demo'. */
  open: (source?: ContactSource) => void;
}

const ContactDialogContext = React.createContext<ContactDialogContextValue | null>(null);

/** Access the shared contact dialog. Must be called inside a ContactDialogProvider. */
export function useContactDialog(): ContactDialogContextValue {
  const ctx = React.useContext(ContactDialogContext);
  if (!ctx) {
    throw new Error('useContactDialog must be used within a ContactDialogProvider');
  }
  return ctx;
}

export const ContactDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [source, setSource] = React.useState<ContactSource>('demo');

  const value = React.useMemo<ContactDialogContextValue>(
    () => ({
      open: (nextSource: ContactSource = 'demo') => {
        setSource(nextSource);
        setIsOpen(true);
      },
    }),
    [],
  );

  return (
    <ContactDialogContext.Provider value={value}>
      {children}
      <DemoRequestDialog open={isOpen} source={source} onClose={() => setIsOpen(false)} />
    </ContactDialogContext.Provider>
  );
};

export default ContactDialogProvider;
